'use client'

import Card from './Card'
import { Product } from '@/payload-types'
import Image from 'next/image'
import Link from 'next/link'
import ListHeader from './ListHeader'
import { useState, useEffect } from 'react'
import { searchProductsByQuery } from '@/app/actions/collections/products'
import { Button } from '../shadcn/button'

interface ProductQuery {
	category?: string;
	subcategory?: string;
	tag?: string;
	creator?: string;
	state?: string;
	size?: string;
	color?: string;
	price?: string;
	page?: number;
	limit?: number;
	sort?: string;
	search?: string;
	new?: boolean;
	[key: string]: string | number | boolean | undefined;
}

interface FilterData {
	colors?: Array<{ id: string; name: string; value: string }>;
	creators?: Array<{ id: string; name: string; value: string }>;
}

export type ListProps = {
	title?: string | Promise<string>
	defaultProducts: Product[]
	link?: string
	query?: ProductQuery
	displayFilters?: boolean
	datas?: FilterData
	wishList?: string[]
	hideIfEmpty?: boolean
	disableRefresh?: boolean
}

export default function List({ title = 'Produits', defaultProducts, link, query = {}, displayFilters = false, datas, wishList, hideIfEmpty = false, disableRefresh = false }: ListProps) {

	const [products, setProducts] = useState(defaultProducts)
	const [filters, setFilters] = useState(query)
	const [page, setPage] = useState(1)
	const [hasMore, setHasMore] = useState(false)
	const [loading, setLoading] = useState(false)

	// Sync local filters state when the incoming query prop changes (e.g., URL updates)
	useEffect(() => {
		setFilters(query || {})
	}, [JSON.stringify(query)])

	useEffect(() => {
		if (disableRefresh) {
			return
		}
		setLoading(true)
		setPage(1)
		searchProductsByQuery({ ...filters, page: 1 }).then((res) => {
			setProducts(res.docs)
			const nextAvailable = (typeof res.hasNextPage !== 'undefined') ? res.hasNextPage : (Array.isArray(res.docs) && res.docs.length >= (res.limit ?? 12))
			setHasMore(Boolean(nextAvailable))
		}).finally(() => setLoading(false))
	}, [filters])


	const updateFilters = (key: string, value: string) => {
		if (value === "" || value === null) {
			const { [key]: _, ...rest } = filters;
			setFilters(rest);
		} else {
			setFilters({ ...filters, [key]: value });
		}
	}

	const loadMore = async () => {
		if (loading) return
		setLoading(true)
		const nextPage = page + 1
		try {
			const res = await searchProductsByQuery({ ...filters, page: nextPage })
			setProducts((prev) => [...prev, ...res.docs])
			setPage(nextPage)
			const nextAvailable = (typeof res.hasNextPage !== 'undefined') ? res.hasNextPage : (Array.isArray(res.docs) && res.docs.length >= (res.limit ?? 12))
			setHasMore(Boolean(nextAvailable))
		} finally {
			setLoading(false)
		}
	}

	return (
		<div className={`flex flex-col gap-6 pt-6 border-t-[1px] border-slate-200 ${hideIfEmpty && products.length === 0 ? 'hidden' : ''}`}>
			<ListHeader title={title as string} link={link} filters={displayFilters} datas={datas} updateFilters={updateFilters} />
			{products.length > 0 ? (
				<>
					<div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-2.5 gap-y-5 mb-4">
						{ products.map((product, index) => <Card key={index} product={product} wish={wishList?.includes(product.id)} />) }
					</div>
					{hasMore && !link && (
						<div className="flex justify-center mb-8">
							<Button
								onClick={loadMore}
								disabled={loading}
								size={'lg'}
								variant={'outline'}
							>
								{loading ? 'Chargement…' : 'Voir plus'}
							</Button>
						</div>
					)}
					{link && (
						<div className="lg:hidden flex justify-center mb-8">
							<Link href={link}>
								<Button size={'lg'} variant={'outline'}>
									VOIR PLUS
								</Button>
							</Link>
						</div>
					)}
				</>
			) : (
				<>
					<div className='w-full p-6 bg-gray-100 shadow-md flex flex-col-reverse md:flex-row gap-8 items-center'>
						<div>
							<h3 className='text-2xl text-black font-alt font-bold mb-4'>
								Aucun résultat trouvé
							</h3>
							<p>
								Malheureusement, il n&apos;y a aucun résultat pour votre recherche. Vous pouvez essayer de modifier vos critères de recherche ou de rechercher un autre produit.
							</p>
						</div>
						<Image src="/assets/images/search_not_found.svg" alt="Aucun résultat" className='shrink-0' width={150} height={150} />
					</div>
				</>
			)}
		</div>
	)
}
